import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEsMovil } from '@/shared/hooks/useEsMovil';
import { cerrarSesion, iniciarSesion, obtenerSesion, type Area } from './sesion.service';

export const CLAVE_SESION = ['sesion'] as const;

/** Sesion actual: `data` es `null` sin login y `undefined` mientras carga. */
export function useSesion() {
  return useQuery({
    queryKey: CLAVE_SESION,
    queryFn: obtenerSesion,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useIniciarSesion() {
  const cola = useQueryClient();
  return useMutation({
    mutationFn: (datos: { usuario: string; contrasena: string }) =>
      iniciarSesion(datos.usuario, datos.contrasena),
    onSuccess: () => cola.invalidateQueries({ queryKey: CLAVE_SESION }),
  });
}

export function useCerrarSesion() {
  const cola = useQueryClient();
  return useMutation({
    mutationFn: cerrarSesion,
    // Se vacia todo el cache: nada de lo que vio un usuario debe quedar para el siguiente.
    onSuccess: () => cola.clear(),
  });
}

/**
 * Permiso del usuario logueado sobre un area, para mostrar u ocultar botones.
 * En celular la app es de solo consulta: ningun permiso de escritura se concede, asi que
 * todo alta, edicion o anulacion que cuelga de este hook desaparece sin tocar cada pantalla.
 */
export function usePermiso(area: Area, escribir = false): boolean {
  const { data } = useSesion();
  const esMovil = useEsMovil();
  const permiso = data?.permisos[area];
  if (!permiso) return false;
  if (!escribir) return permiso.leer;
  return permiso.escribir && !esMovil;
}
